import type { $Fetch } from 'nitropack'
import type { FetchOptions } from 'ofetch'
import { parse } from 'cookie-es'
import { useLocaleStore } from '~/stores/locale'
import type { User } from '~/types/account'
import { useRootStore } from '~/stores/root'
import {useSAuthState} from "~/composables/auth";
import {getStampinupApiDomain} from "~/utils";
import { clearRecruitCookie, getIsRecruitFromCookie } from '~/utils/auth'
import type { NuxtApp } from "#app";
import { useTypedRuntimeConfig } from '~/utils/runtimeConfig'
import { JOIN_ROOT } from '~/utils/joinRoutes'
import { isClient, isServer } from '~/utils/env'

// Cookies the backend consumes on SSR. The browser's `Cookie` header is not replayed by
// ofetch on the server, so without this a hard refresh of a logged-in session loses the
// promo banner that `isNewSubscriber` drives in `cartSummaryOverview` — as nuxt2 did before
// AB#59273. The allowlist is deliberate: only names the backend is known to read are
// forwarded rather than the whole header.
export const FORWARDED_COOKIES = ['isNewSubscriber'] as const

// Values stay as sent: they are replayed to the backend, never read here.
const rawCookies = (header: string | null | undefined, filter: (name: string) => boolean) =>
  parse(header ?? '', { filter, decode: value => value })

export const forwardedCookies = (header: string | undefined) =>
  rawCookies(header, name => (FORWARDED_COOKIES as readonly string[]).includes(name))

// Keyed by cookie name, so applying it again to the headers ofetch carries into
// a retry adds nothing.
export const withAuthCookie = (headersInit: HeadersInit | undefined, token: string | undefined, forwarded: Record<string, string | undefined> = {}) => {
  const headers = new Headers(headersInit)
  const cookies = {
    ...(token ? { auth_cookie: token } : {}),
    ...forwarded,
    ...rawCookies(headers.get('cookie'), name => name !== 'auth_cookie' && !/^path$/i.test(name))
  }

  const parts = Object.entries(cookies).map(([name, value]) => `${name}=${value}`)
  if (parts.length > 0) {
    headers.set('cookie', parts.join('; '))
  }

  return headers
}

/**
 * The SSR-only ofetch tuning, in one place so its values are assertable without
 * booting ofetch (see app/composables/tests/api.spec.ts — mocking the auto-imported
 * `$fetch` is order-dependent under this repo's shared vitest workers).
 *
 * Retry is deliberately left to ofetch's own method-aware default on the server
 * rather than a number. A literal `retry: 1` bypasses ofetch's isPayloadMethod
 * guard and would retry POST/PUT/PATCH/DELETE too - an order submitted twice.
 * `undefined` keeps the guard, so GET/HEAD get one retry and payload methods get
 * none. Without it a single connection refused by a terminating backend pod
 * becomes an SSR 500 with no second attempt, even though the replacement pod is
 * usually serving within seconds — the mechanism behind the 2026-08-24 SSR
 * failure burst, where pod churn under load left renders fetching from pods on
 * their way out (nuxt2 AB#63357).
 *
 * The timeout is one deadline for the whole call, retry included - see
 * withDeadline, which fetchInit applies on every attempt.
 *
 * Connection failures reach onError with no response, and ofetch codes those as
 * 500, so 500 has to stay in the status list for the case this exists for. The
 * narrower list drops ofetch's defaults of 408/409/425/429 - retrying a
 * rate-limited or conflicting request buys nothing and adds load to a backend
 * already in trouble. The pause gives kube-proxy time to drop the terminating pod
 * from the Service, and is negligible against the 10s budget.
 *
 * The timeout is SSR only. ofetch has no default, and nothing in the stack
 * cancels an SSR render when the client disconnects - not the app, not
 * nitropack's node-server entry, not h3. So a render the gateway has already
 * given up on keeps running, holding its heap allocations until the outbound call
 * resolves; with no timeout that wait is bounded only by undici's socket
 * timeouts, which are minutes. nginx returns 504 at 15s, so past that the
 * response cannot reach the user and holding it only costs memory. 10s leaves
 * room for the slowest legitimate call observed (Cart/GetCart, p95 ~1s) and its
 * one retry, and keeps any single call inside nginx's window.
 *
 * Client calls keep retry and timeout off: the memory and pod-churn reasoning is
 * server-side, and silently doubling requests or aborting a slow one on a poor
 * mobile connection is its own problem.
 */
export const ssrFetchTuning = (): Pick<FetchOptions, 'retry' | 'retryStatusCodes' | 'retryDelay' | 'timeout'> => ({
  retry: isServer ? undefined : false,
  retryStatusCodes: [500, 502, 503, 504],
  retryDelay: 250,
  timeout: isServer ? 10000 : undefined
})

/**
 * ofetch arms `timeout` for the first attempt only, clears it as soon as response
 * headers arrive, and hands that same signal to its status retry without a new
 * timer - so a retry that stalls waits on nothing. Swapping in a signal that
 * stays armed makes `timeout` bound the whole call. ofetch passes the signal on
 * to the retry, where it is recognised and kept rather than restarted; a signal
 * the caller supplied still cancels alongside it.
 */
const deadlineSignals = new WeakSet<AbortSignal>()

export const withDeadline = (signal: AbortSignal | null | undefined, timeout: number) => {
  if (signal && deadlineSignals.has(signal)) {
    return signal
  }
  const deadline = AbortSignal.timeout(timeout)
  const bounded = signal ? AbortSignal.any([signal, deadline]) : deadline
  deadlineSignals.add(bounded)
  return bounded
}

// The cookie step of fetchInit's onRequest, on its own so a spec can reach it:
// the server-validated auth_cookie plus the allowlisted cookies of the incoming
// request, which ofetch does not replay from the browser on the server. `isServer`
// rather than `import.meta.server` for the same reason - the inlined literal
// leaves the server branch unreachable under vitest.
// The token is read on the server only: in the browser Cookie is a forbidden header and
// the browser sends auth_cookie itself.
export const withRequestCookies = <T extends HeadersInit | undefined>(headers: T) => {
  if (!isServer) {
    return headers
  }
  const token = useSAuthState().token.value
  const forwarded = forwardedCookies(useRequestHeaders(['cookie']).cookie)
  return token || Object.keys(forwarded).length > 0
    ? withAuthCookie(headers, token, forwarded)
    : headers
}

export const fetchInit = (nuxtApp: NuxtApp, baseUrlOverride?: string) => {
  // No dependency tracking here, unlike nuxt2's fetchInit (utils/apiTelemetry.ts).
  // nuxt2 ran the Application Insights v2 SDK, which only patches http/https and
  // never saw an undici fetch, so each call was recorded by hand. Here
  // nitro-applicationinsights registers OpenTelemetry's undici instrumentation,
  // which already records every SSR call - refused connections, DNS failures,
  // timeouts and bodies that stall after the headers included. Hand-tracking on
  // top of that records each call twice.
  return $fetch.create({
    credentials: 'include',
    ...ssrFetchTuning(),
    async onResponseError ({ response }) {
      await nuxtApp.runWithContext(() => {
        if (response.status === 401 && isClient && !response.url.includes('/sign-in')) {
          const { isRecruit, token, data } = useSAuthState()
          if (isRecruit.value || getIsRecruitFromCookie(document.cookie) === 'true') {
            // Session expired — full logout + clear join session to avoid redirect loops
            // The token is server-only and normally unset in the browser; the clear is
            // a defensive no-op there.
            token.value = undefined
            data.value = { role: 'Anonymous' } as unknown as User
            clearRecruitCookie()
            // Use window.location for a hard redirect — router.push gets blocked
            // by the store catch block which runs after this interceptor
            window.location.href = JOIN_ROOT
            return
          }
          const rootStore = useRootStore()
          rootStore.updateTimeoutMenu(true)
        }
      })
    },
    async onRequest ({ options }) {
      if (options.timeout) {
        options.signal = withDeadline(options.signal, options.timeout)
      }
      await nuxtApp.runWithContext(() => {
        options.headers = withRequestCookies(options.headers)
        const localeStore = useLocaleStore()
        const culture = options.culture ?? localeStore.culture
        const $config = useTypedRuntimeConfig()
        const baseUrlDomain = baseUrlOverride
          ?? (import.meta.server && $config.BACKEND_SERVER_URL
            ? $config.BACKEND_SERVER_URL
            : getStampinupApiDomain($config.public.API_PREFIX, localeStore.culture, $config.public.API_PORT))
        options.baseURL = `${baseUrlDomain}/${culture}`
      })

      return Promise.resolve()
    }
  })
}

const $apiWrapper = () => {
  const { $api } = useNuxtApp()
  return $api as $Fetch
}
export const downloadBlob = async (url: string, filename: string) => {
  const content = await $apiWrapper()<string>(url, { responseType: 'blob' })
  const filesaver = await import('file-saver')
  filesaver.saveAs(content, filename)
}

export {
  $apiWrapper as $api
}
